import React, { useState, useEffect, useRef } from 'react';
import { Upload, Filter, Camera, Trash2 } from 'lucide-react';

const HeritageTab = ({ BACKEND_URL }) => {
  const [heritageImages, setHeritageImages] = useState([]);
  const [heritageCategories, setHeritageCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newImage, setNewImage] = useState({ file: null, title: '', description: '', category: 'Other', era: '', tags: '' });
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef();

  const fetchHeritageCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/heritage/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setHeritageCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err.message);
    }
  };

  const fetchHeritageImages = async (category = null) => {
    try {
      const url = category && category !== 'All'
        ? `${BACKEND_URL}/api/admin/heritage/images?category=${encodeURIComponent(category)}`
        : `${BACKEND_URL}/api/admin/heritage/images`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();
      setHeritageImages(data);
    } catch (err) {
      console.error('Error fetching heritage images:', err.message);
      setHeritageImages([]);
    }
  };

  useEffect(() => {
    fetchHeritageCategories();
    fetchHeritageImages();
  }, []);

  const handleUploadImage = async () => {
    if (!newImage.file || !newImage.title) {
      alert('Please select an image and enter a title');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', newImage.file);
      formData.append('title', newImage.title);
      formData.append('description', newImage.description);
      formData.append('category', newImage.category);
      formData.append('era', newImage.era);
      formData.append('tags', newImage.tags);
      const response = await fetch(`${BACKEND_URL}/api/admin/heritage/images`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Failed to upload image');
      const uploadedImage = await response.json();
      setHeritageImages([uploadedImage, ...heritageImages]);
      setNewImage({ file: null, title: '', description: '', category: 'Other', era: '', tags: '' });
      setShowUploadModal(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      alert('Image uploaded successfully!');
    } catch (err) {
      alert('Error uploading image: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image? This cannot be undone.')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/heritage/images/${imageId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete image');
      setHeritageImages(heritageImages.filter(img => img.id !== imageId));
      setSelectedImage(null);
      alert('Image deleted successfully!');
    } catch (err) {
      alert('Error deleting image: ' + err.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 data-testid="heritage-title" className="text-2xl font-bold text-[#1a4d2e]">Heritage Image Library</h2>
            <p className="text-gray-600">Store and organize your family farming photos for marketing campaigns</p>
          </div>
          <button onClick={() => setShowUploadModal(true)} data-testid="upload-image-btn" className="flex items-center gap-2 bg-[#1a4d2e] text-white px-4 py-2 rounded-lg hover:bg-[#1a4d2e]/90 transition-colors">
            <Upload className="w-4 h-4" /> Upload Image
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-4 mb-6 pb-4 border-b">
          <Filter className="w-5 h-5 text-gray-500" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setSelectedCategory('All'); fetchHeritageImages('All'); }} className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${selectedCategory === 'All' ? 'bg-[#1a4d2e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>All</button>
            {heritageCategories.map((cat) => (
              <button key={cat} onClick={() => { setSelectedCategory(cat); fetchHeritageImages(cat); }} className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${selectedCategory === cat ? 'bg-[#1a4d2e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{cat}</button>
            ))}
          </div>
        </div>

        {heritageImages.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No Heritage Images Yet</h3>
            <p className="text-gray-500 mb-4">Upload your family farming photos to build your story</p>
            <button onClick={() => setShowUploadModal(true)} className="inline-flex items-center gap-2 bg-[#1a4d2e] text-white px-4 py-2 rounded-lg hover:bg-[#1a4d2e]/90 transition-colors">
              <Upload className="w-4 h-4" /> Upload Your First Image
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {heritageImages.map((image) => (
              <div key={image.id} className="group relative aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setSelectedImage(image)}>
                <img src={`${BACKEND_URL}${image.file_path}`} alt={image.title} className="w-full h-full object-cover" onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23ddd" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-size="12">No Image</text></svg>'; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-white font-semibold text-sm truncate">{image.title}</p>
                    {image.era && <p className="text-white/80 text-xs">{image.era}</p>}
                    <span className="inline-block bg-white/20 text-white text-xs px-2 py-0.5 rounded mt-1">{image.category}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-[#1a4d2e] mb-4">Upload Heritage Image</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Image *</label>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={(e) => setNewImage({...newImage, file: e.target.files[0]})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                {newImage.file && <p className="text-sm text-green-600 mt-1">{newImage.file.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={newImage.title} onChange={(e) => setNewImage({...newImage, title: e.target.value})} placeholder="e.g., Grandad mustering, 1962" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Story / Description</label>
                <textarea value={newImage.description} onChange={(e) => setNewImage({...newImage, description: e.target.value})} placeholder="Tell the story behind this photo..." rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select value={newImage.category} onChange={(e) => setNewImage({...newImage, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {heritageCategories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Era / Year</label>
                <input type="text" value={newImage.era} onChange={(e) => setNewImage({...newImage, era: e.target.value})} placeholder="e.g., 1960s, circa 1975" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                <input type="text" value={newImage.tags} onChange={(e) => setNewImage({...newImage, tags: e.target.value})} placeholder="e.g., family, horses, summer" className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowUploadModal(false); setNewImage({ file: null, title: '', description: '', category: 'Other', era: '', tags: '' }); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleUploadImage} disabled={uploadingImage} className="px-4 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#1a4d2e]/90 transition-colors disabled:opacity-50">{uploadingImage ? 'Uploading...' : 'Upload Image'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Image Detail Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedImage(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col md:flex-row">
              <div className="md:w-2/3 bg-black flex items-center justify-center">
                <img src={`${BACKEND_URL}${selectedImage.file_path}`} alt={selectedImage.title} className="max-w-full max-h-[60vh] object-contain" />
              </div>
              <div className="md:w-1/3 p-6 overflow-y-auto">
                <h3 className="text-xl font-bold text-[#1a4d2e] mb-2">{selectedImage.title}</h3>
                {selectedImage.era && <p className="text-gray-500 text-sm mb-4">{selectedImage.era}</p>}
                <span className="inline-block bg-[#1a4d2e]/10 text-[#1a4d2e] text-sm px-3 py-1 rounded-full mb-4">{selectedImage.category}</span>
                {selectedImage.description && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 mb-1">Story</h4>
                    <p className="text-gray-600 text-sm">{selectedImage.description}</p>
                  </div>
                )}
                {selectedImage.tags && selectedImage.tags.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 mb-1">Tags</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedImage.tags.map((tag, idx) => <span key={idx} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">{tag}</span>)}
                    </div>
                  </div>
                )}
                <div className="text-xs text-gray-400 mb-4">Uploaded: {new Date(selectedImage.created_at).toLocaleDateString()}</div>
                <div className="flex gap-2">
                  <button onClick={() => setSelectedImage(null)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">Close</button>
                  <button onClick={() => handleDeleteImage(selectedImage.id)} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeritageTab;
